import { useState, useEffect } from 'react'
import { useEffect as useEffectHook } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithParams, getSPDataWithoutParams } from 'src/supportPortal/services/services'
import { selectEmrSectionsData } from 'src/clinic/reducers/prescription/prescriptionReducer'
import { fetchEmrSections } from 'src/clinic/reducers/prescription/services/emrSectionThunk'
import ViewOnlyPrescriptionV2 from 'src/mentor/components/PrescriptionMakingPage/viewOnlyComponents/ViewOnlyPrescriptionV2'

export default function PrescriptionViewer({ bookedEventId, patientId, bookedEventSlug, intervention, onError }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [prescriptionData, setPrescriptionData] = useState(null)
  const [emrSections, setEmrSections] = useState([])
  const [bookedEventInfo, setBookedEventInfo] = useState(null)

  // Same layout source as the prescription making page (specialization-aware EMR section
  // resolution), via the support variant of the fetchEmrSections thunk for this practitioner.
  const dispatch = useDispatch()
  const emrSectionsStoreData = useSelector(selectEmrSectionsData)
  const doctorConfiguredEmrSections =
    emrSectionsStoreData?.data?.prescription?.emrSections || null
  const reportPractitionerId = bookedEventInfo?.mentorId
  useEffect(() => {
    if (reportPractitionerId) {
      dispatch(fetchEmrSections({ practitionerId: reportPractitionerId, forceUpdateSections: true }))
    }
  }, [reportPractitionerId, dispatch])

  useEffect(() => {
    // Get bookedEventId from props or intervention
    const eventId = bookedEventId || intervention?.bookedEventId
    const eventSlug = bookedEventSlug || intervention?.bookedEventSlug
    const patId = patientId || intervention?.patient?.patientId

    if (!eventId && !eventSlug) {
      if (onError) {
        onError()
        return
      }
      setError('No booked event information available')
      return
    }

    const fetchPrescription = async () => {
      setLoading(true)
      setError(null)

      try {
        // Get values from props or intervention
        const eventId = bookedEventId || intervention?.bookedEventId
        const eventSlug = bookedEventSlug || intervention?.bookedEventSlug
        const patId = patientId || intervention?.patient?.patientId

        // Try to fetch using booked event slug first (preferred method)
        if (eventSlug) {
          try {
            const response = await getSPDataWithParams(
              '/api/secure/cc/support/practitioner-supervisor/booked-slot/details',
              {
                slug: eventSlug,
                'send-auxiliary-data': true,
              },
            )

            if (response.status === 200) {
              // Check if response indicates role/permission error
              if (!isCommonResponseSuccessfulV2(response)) {
                const errorMessage =
                  response?.data?.message || response?.data?.error || response?.data?.description || ''
                if (
                  errorMessage.toLowerCase().includes('role') ||
                  errorMessage.toLowerCase().includes('permission') ||
                  errorMessage.toLowerCase().includes('access') ||
                  errorMessage.toLowerCase().includes('authorized') ||
                  errorMessage.toLowerCase().includes('therapy supervisor')
                ) {
                  // Call onError callback to trigger PDF fallback
                  if (onError) {
                    onError()
                    return
                  }
                  setError(
                    'You do not have the required role to access this prescription. You need to be a Therapy Supervisor to access this API.',
                  )
                  setLoading(false)
                  return
                }
                // If not a role error but still unsuccessful, trigger fallback
                if (onError) {
                  onError()
                  return
                }
              }

              if (isCommonResponseSuccessfulV2(response)) {
                const data = getDataMapFromCommonResponse(response)
                setBookedEventInfo(data?.bookedEvent)
                // Section LAYOUT (defs) only — emrSectionData/screening__EmrSectionData are saved
                // per-event DATA records; using them as layout renders a blank pad.
                setEmrSections(data?.doctorEmrSections || [])

                // If this is a consultation, get prescription from booked event details response
                if (data?.bookedEvent?.eventType === 'consultation') {
                  // Prescription data comes from the booked event details response
                  const prescriptionFromResponse = data?.metaPrescription || data?.auxiliaryData?.metaPrescription
                  if (prescriptionFromResponse) {
                    setPrescriptionData(prescriptionFromResponse)
                    // EMR sections are already set above from the booked event details
                  }
                }

                setLoading(false)
                return
              }
            } else {
              // Response status is not 200 - trigger PDF fallback
              if (onError) {
                onError()
                return
              }
            }
          } catch (slugErr) {
            console.log('Failed to fetch using slug, trying alternative method:', slugErr)
            // Check if it's a role/permission error
            if (slugErr?.response?.status === 403 || slugErr?.response?.status === 401) {
              const errorMessage = slugErr?.response?.data?.message || slugErr?.response?.data?.error || ''
              if (
                errorMessage.toLowerCase().includes('role') ||
                errorMessage.toLowerCase().includes('permission') ||
                errorMessage.toLowerCase().includes('access') ||
                errorMessage.toLowerCase().includes('authorized')
              ) {
                // Call onError callback to trigger PDF fallback
                if (onError) {
                  onError()
                  return
                }
                setError(
                  'You do not have the required role to access this prescription. You need to be a Therapy Supervisor to access this API.',
                )
                setLoading(false)
                return
              }
            }
            // For other errors, continue to fallback method (don't trigger PDF yet)
          }
        }

        // Fallback: Try to fetch booked event details using eventId (if slug method failed)
        if (eventId) {
          try {
            const response = await getSPDataWithParams(
              '/api/secure/cc/support/practitioner-supervisor/booked-slot/details',
              {
                eventId,
                'send-auxiliary-data': true,
              },
            )

            if (response.status === 200) {
              // Check if response indicates role/permission error
              if (!isCommonResponseSuccessfulV2(response)) {
                const errorMessage =
                  response?.data?.message || response?.data?.error || response?.data?.description || ''
                if (
                  errorMessage.toLowerCase().includes('role') ||
                  errorMessage.toLowerCase().includes('permission') ||
                  errorMessage.toLowerCase().includes('access') ||
                  errorMessage.toLowerCase().includes('authorized') ||
                  errorMessage.toLowerCase().includes('therapy supervisor')
                ) {
                  // Call onError callback to trigger PDF fallback
                  if (onError) {
                    onError()
                    return
                  }
                  setError(
                    'You do not have the required role to access this prescription. You need to be a Therapy Supervisor to access this API.',
                  )
                  setLoading(false)
                  return
                }
                // If not a role error but still unsuccessful, trigger fallback
                if (onError) {
                  onError()
                  return
                }
              }

              if (isCommonResponseSuccessfulV2(response)) {
                const data = getDataMapFromCommonResponse(response)
                setBookedEventInfo(data?.bookedEvent)
                // Section LAYOUT (defs) only — emrSectionData/screening__EmrSectionData are saved
                // per-event DATA records; using them as layout renders a blank pad.
                setEmrSections(data?.doctorEmrSections || [])

                // Get prescription from response if it's a consultation
                if (data?.bookedEvent?.eventType === 'consultation') {
                  const prescriptionFromResponse = data?.metaPrescription || data?.auxiliaryData?.metaPrescription
                  if (prescriptionFromResponse) {
                    setPrescriptionData(prescriptionFromResponse)
                  } else {
                    // Prescription not found - trigger PDF fallback
                    if (onError) {
                      onError()
                      return
                    }
                    setError('Prescription not found for this consultation')
                  }
                }
              } else {
                // Failed to load booked event details - trigger PDF fallback
                if (onError) {
                  onError()
                  return
                }
                setError('Failed to load booked event details')
              }
            } else {
              // Call onError callback to trigger PDF fallback
              if (onError) {
                onError()
                return
              }
              setError('Failed to load booked event details')
            }
          } catch (fallbackErr) {
            console.error('Error fetching booked event details:', fallbackErr)
            // Call onError callback for any error to trigger PDF fallback
            if (onError) {
              onError()
              return
            }
            setError('Failed to load prescription data')
          }
        } else {
          // Call onError callback if insufficient info
          if (onError) {
            onError()
            return
          }
          setError('Insufficient information to fetch prescription')
        }
      } catch (err) {
        console.error('Error fetching prescription:', err)
        // Check if it's a role/permission error
        if (err?.response?.status === 403 || err?.response?.status === 401) {
          const errorMessage = err?.response?.data?.message || err?.response?.data?.error || ''
          if (
            errorMessage.toLowerCase().includes('role') ||
            errorMessage.toLowerCase().includes('permission') ||
            errorMessage.toLowerCase().includes('access') ||
            errorMessage.toLowerCase().includes('authorized')
          ) {
            // Call onError callback to trigger PDF fallback
            if (onError) {
              onError()
              return
            }
            setError(
              'You do not have the required role to access this prescription. You need to be a Therapy Supervisor to access this API.',
            )
            setLoading(false)
            return
          }
        }
        // Call onError callback for any error to trigger PDF fallback
        if (onError) {
          onError()
          return
        }
        setError(
          err?.response?.data?.message ||
            'An error occurred while loading the prescription',
        )
      } finally {
        setLoading(false)
      }
    }

    fetchPrescription()
  }, [bookedEventId, patientId, bookedEventSlug, intervention, onError])

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          minHeight: '200px',
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading prescription...
        </Typography>
      </Box>
    )
  }

  if (error) {
    // If onError callback is provided, it should have been called already
    // Just show the error message
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Box>
    )
  }

  if (!prescriptionData) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            No prescription available for this consultation.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The prescription may not have been created yet, or the consultation may not have been completed.
          </Typography>
        </Alert>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        padding: { xs: 2, sm: 3 },
        backgroundColor: '#f9fafb',
        minHeight: '100%',
      }}
    >
      {/* A4 print preview, same as the making page's Quick Print drawer. */}
      <Box sx={{ overflowX: 'auto', backgroundColor: '#f5f5f5', p: 2, borderRadius: 2 }}>
        <Box
          sx={{
            minWidth: '750px',
            margin: '0 auto',
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <ViewOnlyPrescriptionV2
            prescriptionData={prescriptionData}
            doctorEmrSections={
              doctorConfiguredEmrSections?.length ? doctorConfiguredEmrSections : emrSections
            }
            showTheA4Page
            hidePrintMarginsControl={true}
            practitionerId={reportPractitionerId}
          />
        </Box>
      </Box>
    </Box>
  )
}
